import { Box, Stack } from '@mui/material'
import type { ReactNode } from 'react'

interface ZoneProps {
  /** Full testid: row-1 zones keep the wave-3 `control-card-${id}` contract;
   *  row-2 clusters use `control-row2-${id}`. */
  testId: string
  flex?: number | string
  /** Floor below which the zone wraps whole to the next line instead of crushing its controls into stacked rows (wave-4 gate round 4). */
  minWidth?: number | string
  children: ReactNode
}

/** Wave-4 §1 (D2): soft neutral zone card — grouping is carried by the grey.100 fill
 *  (JL's "soft grouping"), not by eyebrow titles or dividers; the controls' own
 *  floating labels (KPI / Timeframe / From / To / Compare with / Group by) name the
 *  contents. The fill is theme-neutral, deliberately NOT brand-derived. */
export function ControlZone({ testId, flex = '0 1 auto', minWidth = 0, children }: ZoneProps) {
  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        bgcolor: 'grey.100',
        minWidth,
        flex,
      }}
    >
      {children}
    </Box>
  )
}

interface BandProps {
  /** Row 1 — the query: what you're looking at (KPI · window · compare · dims). */
  children: ReactNode
  /** Row 2 — presentation + conditionals (granularity, baseline, compare-period,
   *  multi-TF). Pass null when empty; the row only renders when it has content. */
  row2?: ReactNode
}

export function ControlBand({ children, row2 }: BandProps) {
  return (
    <Box
      data-testid="control-band"
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}
    >
      <Stack direction="row" flexWrap="wrap" useFlexGap alignItems="stretch" spacing={1}>
        {children}
      </Stack>
      {row2 ? (
        <Stack
          direction="row"
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          spacing={1}
          data-testid="control-band-row2"
          sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1, pt: 1 }}
        >
          {row2}
        </Stack>
      ) : null}
    </Box>
  )
}
